// src/lib/services/data/smartUpdate.ts
// Atomic state update utility — the ONLY place that writes to domainState.alerts and domainState.workOrders.
// Handles snapshot updates, delta updates, and grouped array rebuilds.

import type { Alert } from '$lib/types/domain/alerts.types';
import type { AlertGroup } from '$lib/types/core/state.types';
import type { AssetFinancialData } from '$lib/types/domain/alerts.types';
import { domainState, buildHiddenAlertIds, isSiteOutage } from '$lib/state/index.svelte';
import { createLogger } from '$lib/utils/logger';
import { browser } from '$app/environment';

const log = createLogger('smartUpdate');

const CACHE_KEY = 'amos:alerts:lastKnownGood';

// Track last processed snapshot timestamp for stale rejection
let lastSnapshotTs = '';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface BackendGroup {
	asset_id: string;
	asset_name: string;
	manager: string;
	alerts: Record<string, unknown>[];
	daily_data?: Record<string, unknown>[];
}

/**
 * Flatten backend grouped alerts into a flat Alert[] array.
 * Copies `manager` from the group onto each alert.
 */
function flattenGroups(groups: BackendGroup[]): Alert[] {
	const flat: Alert[] = [];
	for (const group of groups) {
		if (!group.alerts || !Array.isArray(group.alerts)) continue;
		for (const rawAlert of group.alerts) {
			flat.push({
				...(rawAlert as unknown as Alert),
				manager: (rawAlert as Record<string, unknown>).manager as string ?? group.manager
			});
		}
	}
	return flat;
}

/**
 * Extract financial data (daily_data) from all backend groups.
 * Adds `asset_id` and `asset_name` to each entry.
 */
function extractFinancialData(groups: BackendGroup[]): AssetFinancialData[] {
	const data: AssetFinancialData[] = [];
	for (const group of groups) {
		if (!group.daily_data || !Array.isArray(group.daily_data)) continue;
		for (const entry of group.daily_data) {
			data.push({
				...(entry as unknown as AssetFinancialData),
				asset_id: group.asset_id,
				asset_name: group.asset_name
			});
		}
	}
	return data;
}

/**
 * Build AlertGroup[] from a flat Alert[] array.
 *
 * 1. Merge alerts with same asset_id into one group
 * 2. Filter out alerts hidden by Site Outage overlap
 * 3. Skip empty groups (all alerts hidden)
 * 4. Populate all fields required by the AlertGroup interface
 */
function buildGroups(flatAlerts: Alert[]): AlertGroup[] {
	if (flatAlerts.length === 0) return [];

	// Build hidden alert IDs set (Site Outage + Tracker overlap filtering)
	const hiddenIds = buildHiddenAlertIds(flatAlerts);

	// Group by asset_id
	const byAsset = new Map<string, { alerts: Alert[]; group: BackendGroup | null }>();
	for (const alert of flatAlerts) {
		const key = alert.asset_id;
		let entry = byAsset.get(key);
		if (!entry) {
			entry = { alerts: [], group: null };
			byAsset.set(key, entry);
		}
		entry.alerts.push(alert);
	}

	const result: AlertGroup[] = [];

	for (const [assetId, entry] of byAsset) {
		const allAlerts = entry.alerts;

		// Separate site outages from non-outage alerts
		const siteOutages = allAlerts.filter((a) => isSiteOutage(a.alert_type));
		const nonOutageAlerts = allAlerts.filter(
			(a) => !isSiteOutage(a.alert_type) && !hiddenIds.has(a.alert_flag_id)
		);

		// Skip empty groups (all non-outage alerts hidden by overlap)
		if (nonOutageAlerts.length === 0 && siteOutages.length === 0) continue;

		// Visible alerts = site outages + non-hidden non-outage alerts
		const visibleAlerts = [...siteOutages, ...nonOutageAlerts];

		// Determine representative values from the first alert in the group
		const first = allAlerts[0];
		const assetName = first.asset_name || '';
		const manager = first.manager || '';

		// Determine mixed alert type
		const alertTypes = new Set(visibleAlerts.map((a) => a.alert_type));
		const alertType = alertTypes.size === 1 ? [...alertTypes][0] : 'Mixed';

		result.push({
			id: assetId,
			asset_id: assetId,
			asset_name: assetName,
			asset: { id: assetId, name: assetName },
			alerts: visibleAlerts,
			site_outages: siteOutages,
			non_outage_alerts: nonOutageAlerts,
			count: visibleAlerts.length,
			isGroup: true,
			manager,
			alert_type: alertType
		});
	}

	return result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Process a full snapshot from the backend (DATA_SNAPSHOT).
 *
 * @param groupedRegular  - Backend grouped regular_alerts array
 * @param groupedApproved - Backend grouped approved_alerts array
 * @param snapshotTs      - ISO timestamp string for stale rejection
 * @returns true if the snapshot was applied, false if rejected
 */
export function smartUpdateAlerts(
	groupedRegular: BackendGroup[],
	groupedApproved: BackendGroup[],
	snapshotTs?: string
): boolean {
	// Stale rejection: skip if incoming snapshot is older than last processed
	if (snapshotTs && lastSnapshotTs && snapshotTs < lastSnapshotTs) {
		log.debug('Stale snapshot rejected', { incoming: snapshotTs, last: lastSnapshotTs });
		return false;
	}

	// Flatten groups into flat arrays
	const flatRegular = flattenGroups(groupedRegular ?? []);
	const flatApproved = flattenGroups(groupedApproved ?? []);

	// Never overwrite non-empty with empty
	const existingRegular = domainState.alerts.items;
	const existingApproved = domainState.alerts.approvedItems;

	const useRegular =
		flatRegular.length > 0 || existingRegular.length === 0 ? flatRegular : existingRegular;
	const useApproved =
		flatApproved.length > 0 || existingApproved.length === 0 ? flatApproved : existingApproved;

	// Extract financial data from both regular and approved groups
	const financialRegular = extractFinancialData(groupedRegular ?? []);
	const financialApproved = extractFinancialData(groupedApproved ?? []);
	const allFinancial = [...financialRegular, ...financialApproved];

	// Deduplicate financial data by asset_id + timestamp
	const financialMap = new Map<string, AssetFinancialData>();
	for (const entry of allFinancial) {
		const key = `${entry.asset_id}:${entry.timestamp}`;
		financialMap.set(key, entry);
	}
	const financialData = [...financialMap.values()];

	// Build grouped arrays for UI rendering
	const groupedItems = buildGroups(useRegular);
	const groupedApprovedItems = buildGroups(useApproved);

	// Atomic assignment — all 6 arrays in one synchronous block
	domainState.alerts.items = useRegular;
	domainState.alerts.approvedItems = useApproved;
	domainState.alerts.groupedItems = groupedItems;
	domainState.alerts.groupedApprovedItems = groupedApprovedItems;
	domainState.alerts.financialData = financialData;
	domainState.alerts.isLoading = false;
	domainState.alerts.initialized = true;
	domainState.alerts.lastUpdateTime = new Date();

	// Update stale rejection tracker
	if (snapshotTs) {
		lastSnapshotTs = snapshotTs;
	}

	log.debug('Snapshot applied', {
		regular: useRegular.length,
		approved: useApproved.length,
		groupedRegular: groupedItems.length,
		groupedApproved: groupedApprovedItems.length,
		financial: financialData.length,
		snapshotTs
	});

	// localStorage cache — save successful snapshot
	if (browser) {
		try {
			const cachePayload = {
				regular_alerts: groupedRegular,
				approved_alerts: groupedApproved,
				snapshot_ts: snapshotTs,
				cached_at: new Date().toISOString()
			};
			localStorage.setItem(CACHE_KEY, JSON.stringify(cachePayload));
		} catch (e) {
			// localStorage may be full or unavailable — non-fatal
			log.debug('Failed to cache snapshot to localStorage', { error: e });
		}
	}

	return true;
}

/**
 * Apply a single alert delta (approve/reject/resolve action).
 *
 * 1. Determine target list based on is_verified / status
 * 2. Remove alert from BOTH flat lists by alert_flag_id
 * 3. Add to correct flat list (or neither if resolved/rejected)
 * 4. Rebuild BOTH grouped arrays atomically
 */
export function applyAlertDelta(action: string, alert: Alert): void {
	if (!alert || !alert.alert_flag_id) {
		log.debug('Delta skipped — missing alert or alert_flag_id', { action });
		return;
	}

	const alertFlagId = alert.alert_flag_id;

	// Merge with existing alert data if this is a partial update
	// Backend sometimes sends only changed fields (e.g. work_order_id: null)
	// We need the full alert (especially status/is_verified) to route correctly
	const existingAlert =
		domainState.alerts.items.find((a) => a.alert_flag_id === alertFlagId) ||
		domainState.alerts.approvedItems.find((a) => a.alert_flag_id === alertFlagId);

	const merged: Alert = existingAlert ? { ...existingAlert, ...alert } : alert;

	const status = (merged.status ?? '').toLowerCase();
	const isVerified = merged.is_verified === true;

	// Determine target: approved list, regular list, or neither (filtered out)
	let target: 'approved' | 'regular' | 'none';

	if (isVerified || status === 'approved' || status === 'reverted_wo') {
		target = 'approved';
	} else if (
		(status === 'new' || status === 'open' || status === 'reverted_alert') &&
		!isVerified
	) {
		target = 'regular';
	} else if (existingAlert && !alert.status) {
		// Partial update without status — keep alert in its current list
		// This handles work order revert where backend only sends work_order_id: null
		const wasInApproved = domainState.alerts.approvedItems.some(
			(a) => a.alert_flag_id === alertFlagId
		);
		target = wasInApproved ? 'approved' : 'regular';
	} else {
		// resolved, rejected, closed, etc. — remove from both lists
		target = 'none';
	}

	// Remove from BOTH flat lists
	const filteredRegular = domainState.alerts.items.filter(
		(a) => a.alert_flag_id !== alertFlagId
	);
	const filteredApproved = domainState.alerts.approvedItems.filter(
		(a) => a.alert_flag_id !== alertFlagId
	);

	// Add merged alert to target list (merged = existing data + incoming changes)
	if (target === 'regular') {
		filteredRegular.push(merged);
	} else if (target === 'approved') {
		filteredApproved.push(merged);
	}

	// Rebuild both grouped arrays
	const groupedItems = buildGroups(filteredRegular);
	const groupedApprovedItems = buildGroups(filteredApproved);

	// Atomic assignment — all 4 arrays
	domainState.alerts.items = filteredRegular;
	domainState.alerts.approvedItems = filteredApproved;
	domainState.alerts.groupedItems = groupedItems;
	domainState.alerts.groupedApprovedItems = groupedApprovedItems;
	domainState.alerts.lastUpdateTime = new Date();

	log.debug('Delta applied', {
		action,
		alertFlagId,
		target,
		regular: filteredRegular.length,
		approved: filteredApproved.length
	});
}

/**
 * Process a work order snapshot.
 * Never overwrites non-empty with empty.
 *
 * @returns true if update was applied, false if skipped
 */
export function smartUpdateWorkOrders(items: unknown[]): boolean {
	if (!Array.isArray(items)) {
		log.debug('Work orders update skipped — not an array');
		return false;
	}

	// Never overwrite non-empty with empty
	if (items.length === 0 && domainState.workOrders.items.length > 0) {
		log.debug('Work orders update skipped — would overwrite non-empty with empty');
		return false;
	}

	domainState.workOrders.items = items as any[];
	domainState.workOrders.isLoading = false;
	domainState.workOrders.initialized = true;
	domainState.workOrders.lastUpdateTime = new Date();

	log.debug('Work orders updated', { count: items.length });
	return true;
}
